import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, card } from "./AdminShell";
import {
  Package, CheckCircle2, Circle, Phone, MessageSquare, Wrench, Mail,
  Activity, ArrowRight, ArrowUpRight,
} from "lucide-react";

const StatCard = ({ label, value, icon: Icon, tint, testid }) => (
  <div className={`${card} p-5 hover:shadow-md transition`} data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-neutral-500 mb-2">{label}</div>
        <div className="font-display font-extrabold text-3xl text-neutral-900">{value ?? "—"}</div>
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tint}`}>
        <Icon size={18} />
      </div>
    </div>
  </div>
);

const typeLabel = { product: "Produk", contact: "Kontak", spare_part: "Spare Part" };
const typeLink = { product: "/admin/enquiries/product", contact: "/admin/enquiries/contact", spare_part: "/admin/enquiries/spare-parts" };
const statusBadge = (s) => {
  const map = { "Baru": "bg-[#FF9418]/10 text-[#FF9418]", "Dihubungi": "bg-blue-50 text-blue-700", "Diproses": "bg-purple-50 text-purple-700", "Selesai": "bg-green-50 text-green-700" };
  return map[s] || "bg-neutral-100 text-neutral-600";
};

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const actionLabel = (a) => {
  const map = {
    "login": "Login admin", "logout": "Logout admin",
    "product.created": "Produk dibuat", "product.updated": "Produk diperbarui",
    "product.quick_update": "Quick update produk", "product.deleted": "Produk dihapus",
    "product.duplicated": "Produk digandakan",
    "enquiry.updated": "Enquiry diperbarui", "enquiry.deleted": "Enquiry dihapus",
    "settings.updated": "Pengaturan diperbarui",
    "category.created": "Kategori dibuat", "category.updated": "Kategori diperbarui", "category.deleted": "Kategori dihapus",
    "brand.created": "Brand dibuat", "brand.updated": "Brand diperbarui", "brand.deleted": "Brand dihapus",
    "user.created": "Admin baru ditambahkan", "user.deleted": "Admin dihapus",
    "media.deleted": "File media dihapus",
  };
  return map[a] || a;
};

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/dashboard/stats").then((r) => setStats(r.data)).catch(() => setErr(true));
    api.get("/admin/dashboard/recent").then((r) => setRecent(r.data)).catch(() => setErr(true));
  }, []);

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Ringkasan operasional & aktivitas terbaru" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard label="Total Produk" value={stats?.total_products} icon={Package} tint="bg-neutral-100 text-neutral-700" testid="stat-total" />
        <StatCard label="Tersedia" value={stats?.available_products} icon={CheckCircle2} tint="bg-green-50 text-green-600" testid="stat-available" />
        <StatCard label="Terjual" value={stats?.sold_products} icon={Circle} tint="bg-neutral-100 text-neutral-500" testid="stat-sold" />
        <StatCard label="Hubungi Kami" value={stats?.hubungi_kami} icon={Phone} tint="bg-[#FF9418]/10 text-[#FF9418]" testid="stat-hubungi" />
        <StatCard label="Enquiry Produk Baru" value={stats?.product_enquiries_new} icon={MessageSquare} tint="bg-blue-50 text-blue-600" testid="stat-eq-product-new" />
        <StatCard label="Spare Parts Baru" value={stats?.spare_part_enquiries_new} icon={Wrench} tint="bg-purple-50 text-purple-600" testid="stat-eq-spare-new" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Enquiries */}
        <div className={`${card} lg:col-span-2 overflow-hidden`}>
          <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="font-display font-bold text-neutral-900">Enquiry Terbaru</h2>
            <Link to="/admin/enquiries/product" className="text-xs text-[#FF9418] font-semibold flex items-center gap-1 hover:underline">Lihat semua <ArrowRight size={12} /></Link>
          </div>
          <div className="divide-y divide-neutral-100">
            {!recent && <div className="p-6 text-sm text-neutral-500">Memuat...</div>}
            {recent && recent.recent_enquiries?.length === 0 && <div className="p-6 text-sm text-neutral-500">Belum ada enquiry.</div>}
            {recent?.recent_enquiries?.map((e) => (
              <Link key={e.id} to={typeLink[e.type] || "#"} className="flex items-start gap-3 p-4 hover:bg-neutral-50 transition">
                <div className={`px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider ${statusBadge(e.status)}`}>{e.status}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-neutral-900 text-sm">{e.name}</span>
                    <span className="text-[10px] font-mono uppercase text-neutral-400 tracking-wider">· {typeLabel[e.type] || e.type}</span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5 truncate">
                    {e.product_name || e.subject || e.part_name || ""}
                    {(e.whatsapp || e.city) && ` · ${[e.whatsapp, e.city].filter(Boolean).join(" · ")}`}
                  </div>
                </div>
                <div className="text-[11px] text-neutral-400 font-mono whitespace-nowrap">{formatDate(e.created_at)}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className={`${card} overflow-hidden`}>
          <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="font-display font-bold text-neutral-900 flex items-center gap-2"><Activity size={16} /> Aktivitas</h2>
          </div>
          <div className="divide-y divide-neutral-100 max-h-[400px] overflow-y-auto">
            {!recent && <div className="p-6 text-sm text-neutral-500">Memuat...</div>}
            {recent && recent.recent_activity?.length === 0 && <div className="p-6 text-sm text-neutral-500">Belum ada aktivitas.</div>}
            {recent?.recent_activity?.map((a) => (
              <div key={a.id} className="p-3">
                <div className="text-sm text-neutral-900">{actionLabel(a.action)}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{a.admin_email} · {formatDate(a.created_at)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Products */}
        <div className={`${card} overflow-hidden lg:col-span-2`}>
          <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="font-display font-bold text-neutral-900">Produk Terakhir Diperbarui</h2>
            <Link to="/admin/products" className="text-xs text-[#FF9418] font-semibold flex items-center gap-1 hover:underline">Kelola produk <ArrowRight size={12} /></Link>
          </div>
          <div className="divide-y divide-neutral-100">
            {recent?.recent_updated?.slice(0, 5).map((p) => (
              <Link key={p.id} to={`/admin/products/${p.id}`} className="flex items-center gap-3 p-3 hover:bg-neutral-50 transition">
                <div className="w-10 h-10 bg-neutral-100 rounded flex items-center justify-center text-[9px] text-neutral-400 uppercase font-mono">
                  {p.featured_image ? <img src={p.featured_image} alt="" className="w-full h-full object-cover rounded" /> : (p.brand?.slice(0, 3))}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-neutral-900 text-sm truncate">{p.product_name}</div>
                  <div className="text-xs text-neutral-500">{p.brand} · {p.category}</div>
                </div>
                <ArrowUpRight size={14} className="text-neutral-400" />
              </Link>
            ))}
            {!recent?.recent_updated?.length && <div className="p-6 text-sm text-neutral-500">Belum ada produk.</div>}
          </div>
        </div>

        <div className={`${card} overflow-hidden`}>
          <div className="p-5 border-b border-neutral-200"><h2 className="font-display font-bold text-neutral-900">Produk Terbaru</h2></div>
          <div className="divide-y divide-neutral-100">
            {recent?.recent_products?.slice(0, 5).map((p) => (
              <Link key={p.id} to={`/admin/products/${p.id}`} className="flex items-center gap-2 p-3 hover:bg-neutral-50 transition">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-neutral-900 truncate">{p.product_name}</div>
                  <div className="text-[11px] text-neutral-500">{p.brand}</div>
                </div>
                <ArrowUpRight size={12} className="text-neutral-400" />
              </Link>
            ))}
            {!recent?.recent_products?.length && <div className="p-6 text-sm text-neutral-500">—</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
